import { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import avatar from 'assets/images/avatar.png';
import pdfIcon from 'assets/images/pdf-icon.png';
import commonStrings from 'modules/common/strings';
import SectionDivider from 'modules/common/components/SectionDivider';
import Popup from 'modules/common/components/Popup';

import {
  FilesWrapper,
  FileItemPadding,
  FileImg,
  ResizedFileImg,
  ImgDeleteOverlay,
  ImgDeleteIcon,
  FileItemWrapper,
  ImgPreview,
  Btn,
  LabelWrapper,
  Label,
} from './SingleFileUploader.style';

const FileItem = ({ file, togglePreviewImg, handleFileItemDeleteIconClick }) => {
  const { dataUrl, shouldBeProcessed, isProcessed, dimensions } = file;
  const { dimenParamToChange, ratio } = dimensions;

  let imgSrc = dataUrl || avatar;
  let fileOpacity = (isProcessed && 0.6) || 0.5;
  let fileBlur = (isProcessed && 3) || 0;
  if (shouldBeProcessed === false) {
    imgSrc = pdfIcon;
    fileOpacity = 1;
    fileBlur = 0;
  }

  return (
    <FileItemWrapper onClick={() => togglePreviewImg(true)}>
      <FileItemPadding />
      <FileImg src={imgSrc} opacity={fileOpacity} blur={fileBlur} />
      {isProcessed && (
        <ResizedFileImg src={imgSrc} dimenParamToChange={dimenParamToChange} ratio={ratio} />
      )}
      <ImgDeleteOverlay />
      <ImgDeleteIcon onClick={event => handleFileItemDeleteIconClick(event, file)} />
    </FileItemWrapper>
  );
};
FileItem.propTypes = {
  file: PropTypes.shape({
    dataUrl: PropTypes.string,
    shouldBeProcessed: PropTypes.bool,
    isProcessed: PropTypes.bool,
    dimensions: PropTypes.shape({
      dimenParamToChange: PropTypes.string,
      ratio: PropTypes.number,
    }),
  }).isRequired,
  togglePreviewImg: PropTypes.func.isRequired,
  handleFileItemDeleteIconClick: PropTypes.func.isRequired,
};

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const SUPPORTED_MIME_TYPES_REGEX = /image/i;
const MIME_TYPES_NOT_TO_BE_PROCESSED = ['application/pdf'];
const MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION = ['application/pdf'];
const MAX_IMG_WIDTH = 800;
const MAX_IMG_HEIGHT = 800;
const IMG_QUALITY = 0.7;
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const SingleFileUploader = ({ fileData, setUploadedData }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [isPreviewFilePopupOpen, setIsPreviewFilePopupOpen] = useState(false);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const fileInput = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  const createWindowUrlFromFile = file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);
    return fileUrl;
  };

  const createImageFromFile = fileUrl =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.src = fileUrl;

      image.onload = () => {
        resolve(image);
      };

      image.onerror = reject;
    });

  const resizeAndCompressImage = async image => {
    const { width, height } = image;
    const compressionDimensions = {};
    let dimenParamToChange = '';
    let ratio = 0;
    const absoluteRatio = Math.round((width / height) * 100) / 100;

    if (width > height) {
      const maxWidth = Math.min(width, MAX_IMG_WIDTH);
      compressionDimensions.width = maxWidth;
      compressionDimensions.height = Math.round((height * maxWidth) / width);
      dimenParamToChange = 'height';
      ratio = Math.round((height / width) * 10) / 10;
    } else {
      const maxHeight = Math.min(height, MAX_IMG_HEIGHT);
      compressionDimensions.width = Math.round((width * maxHeight) / height);
      compressionDimensions.height = maxHeight;
      dimenParamToChange = 'width';
      ratio = Math.round((width / height) * 10) / 10;
    }

    const dimensions = {
      dimenParamToChange,
      ratio,
    };

    const canvas = document.createElement('canvas');
    canvas.width = compressionDimensions.width;
    canvas.height = compressionDimensions.height;
    const canvasCtx = canvas.getContext('2d');
    canvasCtx.drawImage(image, 0, 0, compressionDimensions.width, compressionDimensions.height);

    return {
      compressedImageCanvas: canvas,
      dimensions,
      absoluteRatio,
    };
  };

  const createFileFromCanvas = (canvas, fileName, fileType) => {
    const imageDataUrl = canvas.toDataURL(fileType, IMG_QUALITY);
    const imageDataUrlArr = imageDataUrl.split(',');
    const imageMimeType = imageDataUrlArr[0].match(/:(.*?);/)[1];
    const imageBase64 = imageDataUrlArr[1];
    const imageDecodedBase64 = atob(imageBase64);
    let imageDecodedBase64Length = imageDecodedBase64.length;
    const imageBinaryDataArr = new Uint8Array(imageDecodedBase64Length);
    // eslint-disable-next-line no-plusplus
    while (imageDecodedBase64Length--) {
      imageBinaryDataArr[imageDecodedBase64Length] =
        imageDecodedBase64.charCodeAt(imageDecodedBase64Length);
    }
    const compressedImageFile = new File([imageBinaryDataArr], fileName, { type: imageMimeType });

    return {
      compressedImageFile,
      imageDataUrl,
    };
  };

  const processFile = async file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);

    const image = await createImageFromFile(fileUrl);

    let compressedImageCanvas = image;
    let dimensions = {};
    let absoluteRatio = 0;
    if (file.shouldBypassCompression === false)
      ({ compressedImageCanvas, dimensions, absoluteRatio } = await resizeAndCompressImage(image));

    const { compressedImageFile, imageDataUrl } = createFileFromCanvas(
      compressedImageCanvas,
      file.fileName,
      file.fileType,
    );

    const processedFileObj = {
      ...file,
      dataUrl: imageDataUrl,
      isProcessed: true,
      compressedFileData: compressedImageFile,
      dimensions,
      absoluteRatio,
    };

    return processedFileObj;
  };

  const prepareUploadedFileForProcessing = async uploadedFile => {
    let uploadedFileData = { ...uploadedFile };

    if (uploadedFileData.isProcessed === false) {
      // we should use promise.all instead
      // eslint-disable-next-line no-await-in-loop
      uploadedFileData = await processFile(uploadedFileData);
    } else {
      const fileDataUrl = createWindowUrlFromFile(uploadedFileData);
      uploadedFileData = {
        ...uploadedFileData,
        dataUrl: fileDataUrl,
      };
    }

    setUploadedData(uploadedFileData);
  };
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const togglePreviewImg = openPopup => {
    if (openPopup) {
      if (fileData.shouldBeProcessed === false) {
        window.open(window.URL.createObjectURL(fileData.fileData));
      } else {
        setIsPreviewFilePopupOpen(true);
      }
    } else setIsPreviewFilePopupOpen(false);
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleFileInputChange = event => {
    const uploadedFile = event.target.files[0];

    const { name, type } = uploadedFile;

    const isFileTypeSupported = SUPPORTED_MIME_TYPES_REGEX.test(type);

    if (isFileTypeSupported && fileData?.fileName !== name) {
      const shouldBeProcessed = MIME_TYPES_NOT_TO_BE_PROCESSED.includes(type) === false;
      const shouldBypassCompression = MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION.includes(type);

      const fileObj = {
        fileData: uploadedFile,
        fileName: name,
        fileType: type,
        dataUrl: '',
        shouldBeProcessed,
        isProcessed: !shouldBeProcessed,
        shouldBypassCompression,
        compressedFileData: (shouldBypassCompression && uploadedFile) || null,
        dimensions: {},
      };

      if (fileInput && fileInput.current) fileInput.current.value = '';
      setUploadedData(fileObj);
      prepareUploadedFileForProcessing(fileObj);
    }
  };

  const handleFileItemDeleteIconClick = event => {
    if (event) event.stopPropagation();

    setUploadedData(null);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################

  return (
    <>
      <FilesWrapper>
        {fileData && (
          <FileItem
            file={fileData}
            togglePreviewImg={togglePreviewImg}
            handleFileItemDeleteIconClick={handleFileItemDeleteIconClick}
          />
        )}
      </FilesWrapper>
      {isPreviewFilePopupOpen && (
        <Popup handleCloseBtnClick={() => togglePreviewImg()}>
          <ImgPreview src={fileData.dataUrl} />
        </Popup>
      )}
      <SectionDivider gap={30} />
      <Btn
        id="file-uploader-btn"
        type="file"
        accept="image/*"
        ref={fileInput}
        onChange={event => handleFileInputChange(event)}
      />
      <LabelWrapper>
        <Label as="label" htmlFor="file-uploader-btn">
          {commonStrings.singleFileUploader.attachFiles}
        </Label>
      </LabelWrapper>
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

SingleFileUploader.propTypes = {
  fileData: PropTypes.shape({
    fileName: PropTypes.string,
    fileData: PropTypes.shape({}),
    dataUrl: PropTypes.string,
    shouldBeProcessed: PropTypes.bool,
  }),
  setUploadedData: PropTypes.func.isRequired,
};

SingleFileUploader.defaultProps = {
  fileData: null,
};

export default SingleFileUploader;
